import { IconArrowsArrowLeft } from '@doltech/icons/IconArrowsArrowLeft';
import { IconArrowsArrowRight } from '@doltech/icons/IconArrowsArrowRight';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { QuestionGroupGuidelineCollapseExpand } from '@doltech/ui/lib/figma/QuestionGroupGuideline/QuestionGroupGuidelineCollapseExpand';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { QuestionTypeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { isEmpty, noop } from 'lodash';
import * as mobx from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';
import { Cell, Grid, ICellProps } from 'styled-css-grid';

import { SingleAnswer } from '../../../student-do-test-questions/SingleAnswer';
import { Question, QuestionProps } from '../../components/TestContent';
import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import { QuestionDragInMobile } from './DragIn/QuestionDragInMobile';
import { QuestionDragOutMobile } from './DragOut/QuestionDragOutMobile';
import { DoTestCompletionWithHintMobile } from './GapFill/QuestionCompletionWithHint/DoTestCompletionWithHintMobile';
import { DoTestMatchingEndingMobile } from './Matching/QuestionMatchingEnding/DoTestMatchingEndingMobile';
import { DoTestMatchingFeatureNameMobile } from './Matching/QuestionMatchingFeatureName/DoTestMatchingFeatureNameMobile';
import { DoTestMatchingHeadingMobile } from './Matching/QuestionMatchingHeading/DoTestMatchingHeadingMobile';
import { DoTestMatchingParagraphMobile } from './Matching/QuestionMatchingParagraph/DoTestMatchingParagraphMobile';

const GridStyle = styled(Grid)`
  background: ${colorsV2.black5};
  > div {
    background: ${colorsV2.black5};
  }
  .hidden {
    display: none;
  }
`;

const StyledCell = styled(Cell)`
  padding: 0 12px;
  padding-bottom: 78px;
`;
const QuestionOnMobile = observer((props: QuestionProps) => {
  const { item, currentSection } = props;
  const { uuid, type, no } = item;

  const defaultProps = {
    id: `question-no-${no}`,
    key: uuid,
  };

  switch (type) {
    case QuestionTypeEnum.SINGLE_ANSWER: {
      return <SingleAnswer {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_FEATURE_NAME: {
      return (
        <DoTestMatchingFeatureNameMobile
          {...defaultProps}
          data={item}
          currentSection={currentSection}
        />
      );
    }
    case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
      return (
        <DoTestCompletionWithHintMobile
          {...defaultProps}
          currentSection={currentSection}
          data={item}
        />
      );
    case QuestionTypeEnum.MATCHING_HEADING:
      return (
        <DoTestMatchingHeadingMobile
          {...defaultProps}
          currentSection={currentSection}
          data={item}
        />
      );
    case QuestionTypeEnum.MATCHING_ENDING:
      return (
        <DoTestMatchingEndingMobile {...defaultProps} currentSection={currentSection} data={item} />
      );
    case QuestionTypeEnum.MATCHING_PARAGRAPH:
      return (
        <DoTestMatchingParagraphMobile
          {...defaultProps}
          currentSection={currentSection}
          data={item}
        />
      );
    case QuestionTypeEnum.DRAG_IN:
      return <QuestionDragInMobile {...defaultProps} data={item} />;
    case QuestionTypeEnum.DRAG_OUT:
      return <QuestionDragOutMobile {...defaultProps} data={item} />;
    default:
      return <Question item={item} currentSection={currentSection} />;
  }
});

const CellWithBoxShadow = styled(Cell)`
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
  height: auto;
  .collapse-expand-toggle-wrapper {
    position: absolute;
    top: 4px;
    right: 4px;
    button:hover {
      background-color: unset !important;
    }
  }
`;

const QuestionWrapper = styled(Cell)`
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
  > div,
  > div {
    @media ${deviceBreakpoints.mobile} {
      border-radius: 0 !important;
      box-shadow: none !important;
      margin-bottom: 0 !important;
    }
  }

  > div {
    &:first-child {
      margin-top: 2px;
    }
    &:not(:first-child) {
      margin-top: 8px;
    }
  }
`;

const QuestionHeader = styled.div``;

interface QuestionContentMobileProps extends ICellProps {
  onNavigation?: any;
  onSubmit?: () => void;
}

const GroupQuestion = ({ questionGroup, currentSection }) => {
  const questionGroupHeader = questionGroup.getQuestionGroupsFromNoToNo();
  const { content = [], enable = false } = questionGroup.compactGuideline ?? {};
  return (
    <>
      <CellWithBoxShadow>
        <QuestionHeader className="header-container">
          <QuestionGroupGuidelineCollapseExpand
            key={questionGroup.id}
            title={questionGroupHeader}
            content={mobx.toJS(questionGroup.guidelineContent)}
            compactContent={mobx.toJS(content)}
            compactEnable={enable}
            initialExpandValue
          />
        </QuestionHeader>
      </CellWithBoxShadow>
      {questionGroup.questions.map((question, index) => {
        return <QuestionOnMobile key={index} item={question} currentSection={currentSection} />;
      })}
    </>
  );
};

export const QuestionContentMobile = observer((props: QuestionContentMobileProps) => {
  const { onNavigation = noop, onSubmit = noop, ...rest } = props;

  const store = useTestSubmissionStore();
  const activeSection = store.currentSectionIndex;
  const currentSection = store.testSections.find(
    (_section, sectionIndex) => sectionIndex === activeSection
  );
  const currentGroup = currentSection.currentQuestionGroup();

  const renderNextButton = React.useCallback(() => {
    if (isEmpty(store.nextInfo)) {
      return (
        <Button.Primary
          className={cl('btn-submit-do-test', { hidden: !isEmpty(store.nextInfo) })}
          onClick={onSubmit}
          iconRight={<IconArrowsArrowRight />}
          fullWidth
        >
          Submit
        </Button.Primary>
      );
    }
    if (store.isNextPassage) {
      return (
        <Button.Primary
          onClick={() => {
            if (store.preventUserAction()) return;
            onNavigation();
            store.nextQuestionGroup(true);
          }}
          iconRight={<IconArrowsArrowRight />}
          fullWidth
        >
          Next Passage
        </Button.Primary>
      );
    }
    return (
      <Button.Primary
        variant="white"
        onClick={() => {
          if (store.preventUserAction()) return;
          store.nextQuestionGroup(true);
        }}
        iconRight={<IconArrowsArrowRight />}
        fullWidth
      >
        Question {store.nextInfo}
      </Button.Primary>
    );
  }, [onNavigation, onSubmit, store]);

  return (
    <Cell style={{ background: colorsV2.black5 }} {...rest}>
      <GridStyle
        className="questions-container"
        columns="minmax(0, 1fr)"
        rows="max-content auto auto"
      >
        <QuestionWrapper className="question-group">
          {store.skill === SkillEnum.READING ? (
            <GroupQuestion questionGroup={currentGroup} currentSection={currentSection} />
          ) : (
            currentSection.questionGroups.map((group, groupIndex) => {
              return (
                <GroupQuestion
                  key={groupIndex}
                  questionGroup={group}
                  currentSection={currentSection}
                />
              );
            })
          )}
        </QuestionWrapper>
        {store.skill === SkillEnum.READING && (
          <StyledCell className="buttons">
            <Grid
              gap={store.prevInfo.length > 0 ? '16px' : '0'}
              rows={1}
              columns="max-content auto"
              className="btn-question-navigation"
            >
              <Cell>
                {store.prevInfo.length > 0 && (
                  <Button.Primary
                    variant="white"
                    onClick={() => {
                      if (store.preventUserAction()) return;
                      store.prevQuestionGroup(true);
                    }}
                    iconLeft={<IconArrowsArrowLeft />}
                    fullWidth
                  />
                )}
              </Cell>
              <Cell>{renderNextButton()}</Cell>
            </Grid>
          </StyledCell>
        )}
      </GridStyle>
    </Cell>
  );
});
